import React from 'react';
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '' 
}) => {
  // Renderizar conteúdo com suporte a expressões matemáticas
  const renderContentWithMath = () => {
    const parts = [];
    let currentIndex = 0;
    const text = content;

    // Encontrar todas as expressões \[ ... \] (display math)
    const displayMathRegex = /\\\[\s*(.*?)\s*\\\]/gs;
    let match;
    const matches = [];
    
    // Coletar todas as matches
    while ((match = displayMathRegex.exec(text)) !== null) {
      matches.push({
        type: 'display',
        start: match.index,
        end: match.index + match[0].length,
        expression: match[1],
        fullMatch: match[0]
      });
    }

    // Encontrar todas as expressões \( ... \) (inline math)
    const inlineMathRegex = /\\\(\s*(.*?)\s*\\\)/gs;
    while ((match = inlineMathRegex.exec(text)) !== null) {
      matches.push({
        type: 'inline',
        start: match.index,
        end: match.index + match[0].length,
        expression: match[1],
        fullMatch: match[0]
      });
    }

    // Ordenar matches por posição
    matches.sort((a, b) => a.start - b.start);

    // Se não há expressões matemáticas, usar react-markdown normalmente
    if (matches.length === 0) {
      return (
        <ReactMarkdown components={getMarkdownComponents()}>
          {content}
        </ReactMarkdown>
      );
    }

    // Processar o texto intercalando markdown e componentes matemáticos
    matches.forEach((mathMatch, index) => {
      // Adicionar texto antes da expressão matemática
      if (mathMatch.start > currentIndex) {
        const textBefore = text.substring(currentIndex, mathMatch.start);
        if (textBefore.trim()) {
          parts.push(
            <ReactMarkdown key={`text-${index}`} components={getMarkdownComponents()}>
              {textBefore}
            </ReactMarkdown>
          );
        }
      }

      // Adicionar a expressão matemática
      if (mathMatch.type === 'display') {
        parts.push(
          <BlockMath key={`math-${index}`} math={mathMatch.expression} />
        );
      } else {
        parts.push(
          <InlineMath key={`math-${index}`} math={mathMatch.expression} />
        );
      }

      currentIndex = mathMatch.end;
    });

    // Adicionar texto restante
    if (currentIndex < text.length) {
      const textAfter = text.substring(currentIndex);
      if (textAfter.trim()) {
        parts.push(
          <ReactMarkdown key="text-final" components={getMarkdownComponents()}>
            {textAfter}
          </ReactMarkdown>
        );
      }
    }

    return parts;
  };

  const getMarkdownComponents = (): any => ({
    // Custom styling for different elements
    h1: ({children}: any) => (
      <h1 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        {children}
      </h1>
    ),
    h2: ({children}: any) => (
      <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
        {children}
      </h2>
    ),
    h3: ({children}: any) => (
      <h3 className="text-lg font-medium text-gray-800 mb-2 mt-4">
        {children}
      </h3>
    ),
    p: ({children}: any) => (
      <p className="text-gray-700 mb-4 leading-relaxed">
        {children}
      </p>
    ),
    code: ({inline, className, children, ...props}: any) => {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match) {
        // Code block
        return (
          <div className="bg-gray-900 rounded-lg p-4 mb-4 overflow-x-auto">
            <code className="text-sm font-mono text-gray-100" {...props}>
              {children}
            </code>
          </div>
        );
      } else {
        // Inline code
        return (
          <code className="bg-gray-100 text-pink-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
            {children}
          </code>
        );
      }
    },
    blockquote: ({children}: any) => (
      <blockquote className="border-l-4 border-blue-400 bg-blue-50 pl-4 py-2 mb-4 italic text-gray-700">
        {children}
      </blockquote>
    ),
    ul: ({children}: any) => (
      <ul className="list-disc list-inside mb-4 text-gray-700 space-y-1">
        {children}
      </ul>
    ),
    ol: ({children}: any) => (
      <ol className="list-decimal list-inside mb-4 text-gray-700 space-y-1">
        {children}
      </ol>
    ),
    li: ({children}: any) => (
      <li className="ml-4">
        {children}
      </li>
    ),
    a: ({href, children}: any) => (
      <a 
        href={href} 
        className="text-blue-600 hover:text-blue-800 underline" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    strong: ({children}: any) => (
      <strong className="font-semibold text-gray-900">
        {children}
      </strong>
    ),
    em: ({children}: any) => (
      <em className="italic text-gray-800">
        {children}
      </em>
    ),
    table: ({children}: any) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border border-gray-300">
          {children}
        </table>
      </div>
    ),
    thead: ({children}: any) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),
    tbody: ({children}: any) => (
      <tbody className="divide-y divide-gray-200">
        {children}
      </tbody>
    ),
    tr: ({children}: any) => (
      <tr className="hover:bg-gray-50">
        {children}
      </tr>
    ),
    th: ({children}: any) => (
      <th className="px-4 py-2 text-left font-medium text-gray-900 border-b border-gray-300">
        {children}
      </th>
    ),
    td: ({children}: any) => (
      <td className="px-4 py-2 text-gray-700 border-b border-gray-200">
        {children}
      </td>
    ),
    hr: () => (
      <hr className="my-6 border-gray-300" />
    ),
  });

  return (
    <div className={`markdown-content ${className}`}>
      {renderContentWithMath()}
    </div>
  );
};